/* 丁奉（字承淵） */
import { SG, K, gd, spMul, sigOf, landing } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, S } from '../define.ts';

export default defineHero({
  id: 'dingfeng', name: '丁奉', sub: '字承淵', faction: 'wu', role: '刺客', weapon: 'sword',
  look: { knife: true, weaponName: '短兵', head: 'topknot', beard: 'mustache', fx: '#d8f0ff', c: { cape: '#b33a30' } },
  skills: [
    ['短兵突', 'charge', '突進到一名敵人身邊，{80/120/160/200} 傷害；目標生命低於 50% 時冷卻減半。', { x: { k: 'shortdash', dmg: [80, 120, 160, 200] }, aim: U(520), wardHop: true, cd: [10, 9, 8, 7], mp: [55, 60, 65, 70], el: null }],
    ['解甲', 'buff', '卸甲 {5/6/7/8} 秒：防禦歸零，攻擊 +{25/35/45/55}%、攻速 +30%、移速 +13%；期間擊殺或助攻武將就立刻穿回鎧甲並回復 20% 生命。', { x: { k: 'unarmor', dur: [5, 6, 7, 8], atkP: [25, 35, 45, 55] }, aim: S(0), cd: [20, 18, 16, 14], mp: 60, el: null }],
    ['雪中行', 'passive', '被緩速時傷害 +{10/15/20/25}%；冰凍時間減半。', { pv: {}, sig: 'snow', amp: [10, 15, 20, 25] }],
    ['東興奇襲', 'blink', '選擇 900 內生命低於 {30/35/40}% 的敵將，消失 0.8 秒後出現在他身後斬擊 {250/375/500}；若斬殺，3 秒內可以再發動一次。', { x: { k: 'raid', pct: [30, 35, 40], dmg: [250, 375, 500], delay: 0.8 }, aim: U(900), cd: [70, 60, 50], mp: [100, 120, 140], el: 'ice', shout: '雪中奮短兵，破敵就在今日！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    shortdash(u, a, X) {
      const w = this.hopWard(u, a);
      if (w) { this.hopTo(u, w); return; } // 跳眼
      const t = this.xPick(u, a.pt, 160);
      if (!t) return;
      const p = landing(u.x, u.y, t.x - Math.sign(t.x - u.x || 1) * 40, t.y);
      u.x = p[0]; u.y = p[1]; u.f = Math.sign(t.x - u.x) || u.f;
      const low = t.hp < t.maxHp * 0.5;
      this.damage(u, t, X.dmg * spMul(u), { stagger: 0.3 });
      if (low) { const i = u.spec.skills.indexOf(a.sk); u.cd[i] *= 0.5; }
    },
    unarmor(u, a, X) {
      this.addBuff(u, { id: 'unarmor', t: X.dur, atkP: X.atkP / 100, as: 30, ms: 0.13, armor: -Math.max(0, u.armor) });
      u.unarmorT = this.t + X.dur;
      this.fx({ k: 'puff', x: u.x, y: u.y });
    },
    raid(u, a, X) {
      const t = this.xPick(u, a.pt, 180);
      const i = u.spec.skills.indexOf(a.sk);
      if (!t || t.kind !== 'hero' || t.hp > t.maxHp * X.pct / 100) {
        this.tell(u, `東興奇襲：要選生命低於 ${X.pct}% 的敵將`, 'warn');
        u.cd[i] = 1; u.mp = Math.min(u.maxMp, u.mp + this.skillCost(i, a.lvl, a.sk)); // 選錯目標：退還耗氣
        return;
      }
      u.st.invis = Math.max(u.st.invis, X.delay); u.raidT = this.t + X.delay;
      this.fx({ k: 'puff', x: u.x, y: u.y });
      this.later(X.delay, () => {
        if (!u.alive) return;
        if (!t.alive) return;
        const p = landing(t.x, t.y, t.x - (t.f || 1) * 50, t.y);
        u.x = p[0]; u.y = p[1]; u.f = Math.sign(t.x - u.x) || u.f; u.st.invis = 0;
        this.damage(u, t, X.dmg * spMul(u), { stagger: 0.6 });
        this.fx({ k: 'slam', x: t.x, y: t.y, col: u.spec.fx });
        if (!t.alive && !u.raidAgain) { u.raidAgain = this.t + 3; u.raidCd = u.cd[i]; u.cd[i] = 0; this.tell(u, '東興奇襲：斬殺！3 秒內可以再發動', 'info'); }
        else if (u.raidAgain) u.raidAgain = 0; // 第二次之後不再重置
      });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 解甲：擊殺或助攻武將就穿回鎧甲並回血
    hook('kill.after', 31, function (src, t, was) {
      if (!was) return;
      for (const u of this.units) {
        if (!u.alive || !u.unarmorT || u.unarmorT <= this.t || u.team === t.team) continue;
        const killer = src && (src.owner || src);
        if (killer !== u && !(t.dmgBy && t.dmgBy[u.id] > this.t - 10)) continue;
        u.unarmorT = 0;
        this.addBuff(u, { id: 'unarmor', t: 0.01 });
        this.heal(u, u.maxHp * 0.2);
        this.tell(u, '解甲：穿回鎧甲', 'info');
      }
    }),
    // 雪中行：被緩速時加傷
    hook('damage.before', 38, function (c) {
      const s = sigOf(c.own, 'snow');
      if (s && ((c.own.st.slow || 0) > 0 || (c.own.bf && c.own.bf.ms < 0))) c.a *= 1 + s.v('amp') / 100;
    }),
    // 雪中行：冰凍時間減半
    hook('cc.tenacity', 40, function (t, cc) { return sigOf(t, 'snow') && cc && (cc === 'freeze' || cc.freeze) ? 50 : 0; }),
    // 東興奇襲：斬殺後 3 秒沒有再發動，冷卻恢復
    hook('step.hero', 33, function (u, dt) {
      if (u.raidAgain && u.raidAgain <= this.t) {
        const i = 3;
        if (u.cd[i] <= 0) u.cd[i] = Math.max(0, (u.raidCd || 0) - 3);
        u.raidAgain = 0;
      }
    }),
    hook('revive', 75, function (u) { u.unarmorT = 0; u.raidAgain = 0; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    shortdash: ({ t, d, hero }) => !!t && d < 500 && (hero || t.hp / t.maxHp < 0.5),
    unarmor: ({ hero, d, hpR }) => hero && d < 400 && hpR > 0.45,
    raid: ({ hero, t, d }) => hero && !!t && d < 880 && t.hp / t.maxHp < 0.3,
  },
});
